import { useEffect, useRef, useState } from "react";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./curriculum.css";

const MeCurriculum = () => {
  const [curriculum, setCurriculum] = useState();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState({ name: "", size: 0 });
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState("");
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codiceFiscale = searchParams.get("codiceFiscale");
  const [editingNote, setEditingNote] = useState(false);

  const fetchCurriculum = async () => {
    try {
      setLoading(true);
      const response = await api.get("/curriculum", {
        params: codiceFiscale ? { codiceFiscale } : {},
      });
      if (response.status === 200) {
        setCurriculum(response.data);
        setNote(response.data.noteCv || "");
      }
    } catch (error) {
      if (error.status === 404) {
        toast.info(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      alert("Accedi come operatore per visualizzare i curriculum!");

      navigate("/login");
    } else {
      fetchCurriculum();
    }
  }, [navigate]);

  const handleFileChange = (e) => {
    try {
      const selected = e.target.files[0];
      if (!selected) return;

      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(selected.type)) {
        toast.error("Formato file non supportato. Usa PDF, DOC o DOCX.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setFile(null);
        return;
      }

      if (selected.size > maxSize) {
        toast.error(
          `Il file supera la dimensione massima di 5MB. Dimensione attuale: ${(selected.size / (1024 * 1024)).toFixed(1)}MB`,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          }
        );
        setFile(null);
        return;
      }

      setFile(selected);
      setFileInfo({
        name: selected.name,
        size: (selected.size / 1024).toFixed(1),
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante la selezione del file", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Seleziona un file prima di caricare", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await api.post("/curriculum/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: codiceFiscale ? { codiceFiscale } : {},
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percent);
        },
      });

      if (response.status === 200) {
        toast.success("Curriculum caricato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        fetchCurriculum();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
      setProgress(0);
      setFile(null);
      setFileInfo({ name: "", size: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleNoteUpdate = async () => {
    try {
      setLoading(true);
      const response = await api.put(
        "/curriculum",
        { noteCv: note },
        {
          params: codiceFiscale ? { codiceFiscale } : undefined,
        }
      );

      if (response.status === 200) {
        toast.success("Nota aggiornata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setCurriculum(response.data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const params = codiceFiscale ? { codiceFiscale } : {};
      const response = await api.get("/curriculum/download", {
        params,
        responseType: "blob",
      });
      console.log(response.data);

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const contentDisposition = response.headers["content-disposition"];
      let filename = curriculum.filename || "curriculum.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileChange({ target: { files: [droppedFile] } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = () => {
    setFile(null);
    setFileInfo({ name: "", size: 0 });
    fileInputRef.current.value = "";
  };

  const deleteCv = async () => {
    try {
      setLoading(true);
      const response = await api.delete("/curriculum");
      if (response.status === 204) {
        toast.success("Curriculum eliminato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setCurriculum(null);
        setNote("");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="me-cv-modern-bg">
      {loading && (
        <div className="me-cv-modern-overlay">
          <div className="me-cv-modern-spinner"></div>
        </div>
      )}
      <div className="me-cv-modern-card">
        <div className="me-cv-modern-header">
          <h2>
            <i className="fa-solid fa-file-lines"></i>
            Curriculum Vitae
          </h2>
          <button
            className="me-cv-modern-delete"
            onClick={(e) => {
              e.stopPropagation();
              deleteCv();
            }}
            title="Elimina Curriculum"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
        {curriculum && (
          <div className="me-cv-modern-info">
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-id-card"></i> ID
              </span>
              <span className="me-cv-modern-value">
                {curriculum.idCurriculum}
              </span>
            </div>
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-calendar-plus"></i> Data caricamento
              </span>
              <span className="me-cv-modern-value">
                {new Date(curriculum.dataCaricamento).toLocaleString()}
              </span>
            </div>
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-user"></i> Autore
              </span>
              <span className="me-cv-modern-value">{curriculum.autore}</span>
            </div>
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-file"></i> Nome file
              </span>
              <span className="me-cv-modern-value">
                <button
                  className="me-cv-modern-download"
                  onClick={handleDownload}
                >
                  {curriculum.filename}
                  <i className="fa-solid fa-download"></i>
                </button>
              </span>
            </div>
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-file-signature"></i> Tipo
              </span>
              <span className="me-cv-modern-value">
                {curriculum.contentType}
              </span>
            </div>
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-weight-hanging"></i> Dimensione
              </span>
              <span className="me-cv-modern-value">
                {(curriculum.fileSize / 1024).toFixed(1)} KB
              </span>
            </div>
            <div className="me-cv-modern-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-address-card"></i> Codice Fiscale
              </span>
              <span className="me-cv-modern-value">
                {curriculum.codiceFiscale}
              </span>
            </div>
            <div className="me-cv-modern-row me-cv-modern-note-row">
              <span className="me-cv-modern-label">
                <i className="fa-solid fa-note-sticky"></i> Nota CV
              </span>
              <span className="me-cv-modern-value">
                {!editingNote ? (
                  <>
                    <span className="me-cv-modern-note-text">
                      {note || "—"}
                    </span>
                    <button
                      onClick={() => setEditingNote(true)}
                      className="me-cv-modern-icon-btn"
                      title="Modifica nota"
                    >
                      <i className="fa-solid fa-pen-to-square"></i>
                    </button>
                  </>
                ) : (
                  <>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      className="me-cv-modern-notearea"
                    ></textarea>
                    <button
                      onClick={() => setEditingNote(false)}
                      className="me-cv-modern-icon-btn"
                      title="Annulla"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                    <button
                      onClick={async () => {
                        await handleNoteUpdate();
                        setEditingNote(false);
                      }}
                      className="me-cv-modern-icon-btn"
                      title="Salva nota"
                    >
                      <i className="fa-solid fa-floppy-disk"></i>
                    </button>
                  </>
                )}
              </span>
            </div>
          </div>
        )}
        <div className="me-cv-modern-upload-card">
          <form
            className="me-cv-modern-upload-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <div
              className={`me-cv-modern-drag-area${
                file ? " file-selected" : ""
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <span className="me-cv-modern-upload-icon">
                <i
                  className={`fa-solid ${
                    file ? "fa-circle-check" : "fa-cloud-arrow-up"
                  }`}
                ></i>
              </span>
              <span className="me-cv-modern-upload-message">
                {file
                  ? "File rilasciato con successo!"
                  : "Trascina e rilascia un file qui o usa il bottone sotto"}
              </span>
              <label className="me-cv-modern-upload-label">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="me-cv-modern-file-input"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                />
                <span className="me-cv-modern-upload-browse">
                  Scegli file dal dispositivo
                </span>
              </label>
            </div>
            {file && (
              <div className="me-cv-modern-file-block">
                <div className="me-cv-modern-file-info">
                  <i className="fa-solid fa-file-lines"></i>
                  <span className="me-cv-modern-file-name">
                    {fileInfo.name}
                  </span>
                  <span className="me-cv-modern-file-size">
                    {fileInfo.size} KB
                  </span>
                  <button
                    type="button"
                    className="me-cv-modern-file-remove"
                    onClick={removeFile}
                    title="Rimuovi file"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>
                <div className="me-cv-modern-progress-bar-bg">
                  <div
                    className="me-cv-modern-progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}
            <button
              type="button"
              className="me-cv-modern-upload-btn"
              onClick={handleUpload}
              disabled={!file}
            >
              {progress === 100 ? (
                <>
                  <i className="fa-solid fa-circle-check"></i> Caricato
                </>
              ) : (
                <>
                  <i className="fa-solid fa-cloud-arrow-up"></i> Carica
                  Curriculum
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MeCurriculum;
